import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Sparkles, LogOut, Video } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useProjects, usePipeline } from '../hooks/useProject';
import { ProjectCard } from '../components/ProjectCard';
import { PipelineView } from '../components/PipelineView';
import { NewProjectModal } from '../components/NewProjectModal';

interface DashboardPageProps {
  user: { email?: string; [key: string]: any };
}

export default function DashboardPage({ user }: DashboardPageProps) {
  const navigate = useNavigate();
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [showNewProject, setShowNewProject] = useState(false);

  const { projects, loading, createProject, deleteProject } = useProjects();
  const { status: pipelineStatus, startPipeline, approveScenario, stopPipeline } = usePipeline(selectedProjectId);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  const selectedProject = projects.find(p => p.id === selectedProjectId);

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-gradient-to-br from-purple-500 to-blue-500 rounded-lg flex items-center justify-center">
              <Video className="w-4 h-4 text-white" />
            </div>
            <span className="font-semibold text-gray-900">DemoForge</span>
          </div>

          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500">{user.email}</span>
            <button onClick={handleLogout} className="p-1.5 hover:bg-gray-100 rounded-lg">
              <LogOut className="w-4 h-4 text-gray-400" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">프로젝트</h2>
              <button
                onClick={() => setShowNewProject(true)}
                className="px-3 py-1.5 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 transition flex items-center gap-1.5"
              >
                <Plus className="w-4 h-4" /> 새 프로젝트
              </button>
            </div>

            {loading ? (
              <div className="text-center py-12 text-gray-400">
                <div className="w-6 h-6 border-2 border-gray-200 border-t-purple-500 rounded-full animate-spin mx-auto mb-2" />
                불러오는 중...
              </div>
            ) : projects.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-gray-200">
                <Sparkles className="w-10 h-10 text-purple-300 mx-auto mb-3" />
                <h3 className="font-medium text-gray-700 mb-1">아직 프로젝트가 없어요</h3>
                <p className="text-sm text-gray-500 mb-4">
                  웹서비스 URL을 입력하면<br />AI가 데모 영상을 만들어드려요
                </p>
                <button
                  onClick={() => setShowNewProject(true)}
                  className="px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 transition"
                >
                  첫 프로젝트 만들기
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {projects.map(project => (
                  <ProjectCard
                    key={project.id}
                    project={project}
                    onSelect={setSelectedProjectId}
                    onDelete={deleteProject}
                    onStart={(id) => {
                      setSelectedProjectId(id);
                      startPipeline(id);
                    }}
                  />
                ))}
              </div>
            )}
          </div>

          <div className="col-span-7">
            {selectedProject ? (
              <div className="space-y-4">
                {/* 프로젝트 헤더 */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                  <h2 className="text-lg font-semibold mb-2">{selectedProject.name}</h2>
                  <p className="text-sm text-gray-500 mb-4">{selectedProject.url}</p>

                  {selectedProject.status === 'init' && (
                    <button
                      onClick={() => startPipeline()}
                      className="w-full py-3 bg-gradient-to-r from-purple-600 to-blue-500 text-white rounded-xl font-medium hover:from-purple-700 hover:to-blue-600 transition flex items-center justify-center gap-2 shadow-md"
                    >
                      <Sparkles className="w-5 h-5" />
                      AI 데모 영상 생성 시작
                    </button>
                  )}

                  {selectedProject.status === 'error' && (
                    <div className="space-y-3">
                      <div className="bg-red-50 text-red-600 text-sm rounded-xl p-4 text-center">
                        영상 생성에 실패했어요. 다시 시도해주세요.
                      </div>
                      <button
                        onClick={() => startPipeline()}
                        className="w-full py-3 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-700 transition"
                      >
                        다시 시도
                      </button>
                    </div>
                  )}

                  {selectedProject.status === 'complete' && pipelineStatus?.video_url && (
                    <div className="space-y-4">
                      <video
                        src={pipelineStatus.video_url}
                        controls
                        className="w-full rounded-xl shadow-lg"
                      />
                      <a
                        href={pipelineStatus.video_url}
                        download={`${selectedProject.name}-demo.mp4`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block w-full py-3 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 transition text-center"
                      >
                        영상 다운로드 (MP4)
                      </a>
                    </div>
                  )}
                </div>

                {/* 파이프라인 진행 상황 (활성 상태일 때 표시) */}
                {selectedProject.status !== 'init' && pipelineStatus && (
                  <PipelineView
                    projectId={selectedProject.id}
                    status={pipelineStatus.status}
                    progress={pipelineStatus.progress}
                    steps={pipelineStatus.steps}
                    onApprove={approveScenario}
                    onStop={stopPipeline}
                  />
                )}
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
                <Video className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <h3 className="font-medium text-gray-600 mb-1">프로젝트를 선택하세요</h3>
                <p className="text-sm text-gray-400">
                  왼쪽에서 프로젝트를 선택하면<br />진행 상황을 확인할 수 있습니다
                </p>
              </div>
            )}
          </div>
        </div>
      </main>

      <NewProjectModal
        open={showNewProject}
        onClose={() => setShowNewProject(false)}
        onSubmit={async (data) => {
          const project = await createProject(data);
          setSelectedProjectId(project.id);
        }}
      />
    </div>
  );
}
